import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { debounce } from "lodash";
import Pusher, { Channel, Options } from "pusher-js";

import { handlePusherOrderEvents } from "features/orders";
import { callGetReservationsAPI } from "features/reservations/listing";
import { OrderEventDataType } from "features/orders/index.types";
import Notification from "../../../assets/notification/alert.ogg";
import { getLocalStorage } from "../helperFunctions";
import { ORDER_PUSHER_CHANNEL } from "config";
import { pusher_key } from "utils/constants";
import { dispatch, useSelector } from "store";
import { openSnackbar } from "store/slices/snackbar";

interface PusherOptions extends Options {
  encrypted?: boolean;
}

// storing path here to get updated path on pusher callback
let updatedPath = "";

// storing path here to get updated state in playNotificationSound() function
let updatedPusherSubscription = false;

const PusherNotification = () => {
  const { permittedRoutes } = useSelector((state) => state.accessManagement);
  const { businessBranchesAccess } = useSelector(
    (state) => state.businessBranches
  );
  const { pusherSubscription } = useSelector((state) => state.global);

  const { pathname } = useLocation();
  updatedPath = pathname;

  const { eatout_id } = getLocalStorage();

  // creating Pusher instance
  const ordersPusher = new Pusher(pusher_key, {
    cluster: "mt1",
  } as PusherOptions);

  const ordersChanel = `${ORDER_PUSHER_CHANNEL}-${eatout_id}`;
  const audioRef = React.useRef<HTMLAudioElement>(null);

  //======================================= useEffect Hooks =======================================//

  useEffect(() => {
    let ordersSubscription: Channel;
    let reservationsChannel: Channel;

    // storing updated state
    updatedPusherSubscription = pusherSubscription;

    if (updatedPusherSubscription && businessBranchesAccess?.length) {
      // for order notifications if have access
      if (
        permittedRoutes?.some(
          (route) => route === "/orders" || route === "/future-orders"
        )
      ) {
        // handler to manage orders pusher events
        var handleTriggeredOrderEvent = (
          eventName: string,
          data: OrderEventDataType
        ) => {
          // validate data
          if (data && !Array.isArray(data) && typeof data === "object") {
            /**
             * validate order-id existence
             * validate branch access
             */
            if (data.order_id && validateUserAccess(data)) {
              /**
               * if => handle pusher 'new-order' event
               * else if => handle pusher 'update-order' event
               */
              if (eventName === "new-order") {
                // function to play notification sound
                playNotificationSound();

                // setting notify message
                dispatch(
                  openSnackbar({
                    open: true,
                    message: "New Order",
                    alert: {
                      color: "info",
                    },
                  })
                );

                // calling orders API when we are on orders screen
                if (updatedPath === "/orders") {
                  handlePusherOrderEvents("NewOrder", data.order_id);
                }
              } else if (
                eventName === "update-order" &&
                (updatedPath === "/orders" || updatedPath === "/future-orders")
              ) {
                /**
                 * handle order update on 'update-order' pusher event
                 * and when user is on orders or future-orders tab
                 */
                handlePusherOrderEvents("UpdateOrder", data.order_id);
              }
            }
          }
        };

        // subscribing with backend
        ordersSubscription = ordersPusher.subscribe(ordersChanel);

        // bind to all events on the connection
        ordersSubscription.bind_global(handleTriggeredOrderEvent);
      }

      // for reservation notifications if have access
      if (permittedRoutes?.includes("/reservations")) {
        const reservationPusherChanel = "reservation-" + eatout_id;
        const reservationPusherEvent = "my-event-" + eatout_id + "-0";

        // creating Pusher instance
        const reservationsPusher = new Pusher(pusher_key, {
          cluster: "mt1",
          forceTLS: true,
        });

        reservationsChannel = reservationsPusher.subscribe(
          reservationPusherChanel
        );

        reservationsChannel.bind(reservationPusherEvent, () => {
          // notify user
          dispatch(
            openSnackbar({
              open: true,
              message: "New Reservation",
              alert: { color: "info" },
            })
          );

          // calling reservations API when we are on reservations screen
          if (updatedPath === "/reservations") {
            callGetReservationsAPI();
          }
        });
      }
    }

    // component unmount
    return () => {
      if (ordersSubscription) {
        // un-subscribe orders subscribed channel
        ordersSubscription.unsubscribe();
      }

      if (reservationsChannel) {
        // unbinding the reservationsChannel from specific business event
        reservationsChannel.unbind();
      }
    };
  }, [pusherSubscription, permittedRoutes, businessBranchesAccess]);

  //======================================= Handler Functions =======================================//

  // function to play notification sound for orders/reservations
  const playNotificationSound = debounce(() => {
    audioRef.current?.play();

    /**
     * repeat notification bell when:
     * updatedPath !== "/orders" => user not on orders screen
     * document["hidden"] => user not on biz tab
     * updatedPusherSubscription => user not selected any business
     */
    if (
      (updatedPath !== "/orders" || document["hidden"]) &&
      updatedPusherSubscription
    ) {
      playNotificationSound();
    }
  }, 2000);

  /**
   * validate branch access & business id
   * branch_id = 0 => check for user having all branches access
   * branch_id != 0 => check for user having that branch access
   */
  const validateUserAccess = (data: OrderEventDataType) => {
    return (
      data.business_id?.toString() === eatout_id &&
      data.hasOwnProperty("branch_id") &&
      ((data.branch_id === 0 &&
        businessBranchesAccess?.length &&
        !businessBranchesAccess[0].value) ||
        businessBranchesAccess.some(
          (branch) => data.branch_id?.toString() === branch.value
        ))
    );
  };

  return <audio ref={audioRef} src={Notification} preload="auto" />;
};

export default PusherNotification;
